import { useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import * as Sentry from "@sentry/react";
import I18NLink from "@/components/i18n-link";
import { Button } from "@/components/ui/button";
import Header from "./layout/header";
import { useSuspenseQuery } from "@tanstack/react-query";
import { websiteConfigQueryOptions } from "@/lib/website-config";
import { useTranslation } from "react-i18next";

export default function GeneralError({
  error,
}: {
  // TanStack Router passes the thrown error here; it's safe to type as unknown
  error?: unknown;
}) {
  useEffect(() => {
    if (error) {
      Sentry.captureException(error);
    }
  }, [error]);

  const message = getErrorMessage(error);
  const websiteConfig = useSuspenseQuery(websiteConfigQueryOptions());

  //translations
  const { t } = useTranslation("translation");

  return (
    <div className="min-h-dvh flex flex-col">
      <Header
        companyName={websiteConfig.data.companyName}
        logoUrl={websiteConfig.data.logo}
      />
      <div className="grid place-items-center flex-1">
        <div className="w-full max-w-xl rounded-xl border border-black/5 bg-white/80 shadow-md backdrop-blur-sm">
          <div className="p-6 sm:p-8 flex flex-col items-center text-center gap-5">
            <span className="inline-flex items-center justify-center rounded-full bg-amber-100 text-amber-700 size-12">
              <AlertTriangle className="size-6" aria-hidden="true" />
            </span>

            <div className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">
                {t("Error.Global.title")}
              </h1>
              {message ? (
                <p className="text-sm italic text-neutral-600 break-words">
                  {`"${message}"`}
                </p>
              ) : (
                <p className="text-sm text-neutral-600">
                  {t("Error.Global.message")}
                </p>
              )}
            </div>

            <div className="pt-2">
              <Button asChild>
                <I18NLink to="/">{t("Error.Global.back_to_home")}</I18NLink>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getErrorMessage(error: unknown): string | null {
  if (!error) return null;
  if (typeof error === "string") return error;
  if (error instanceof Error && error.message) return error.message;
  try {
    return JSON.stringify(error);
  } catch {
    return null;
  }
}
